import React, { useState, useEffect, useContext } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import LanguageIcon from "@material-ui/icons/Language";
import { existingTicketFromError, canOpenExistingTicket } from "../../utils/existingTicket";
import { filterOutboundConnections } from "../../utils/channels";
import { canSeeTicketConnection, filterAllowedConnections, isConnectionRestricted } from "../../utils/connectionScope";
import ShowTicketOpen from "../ShowTicketOpenModal";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "min(440px, calc(100vw - 32px))",
    maxWidth: "none",
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: "18px 22px 6px",
  },
  title: {
    margin: 0,
    fontSize: 19,
    fontWeight: 700,
    lineHeight: "26px",
    letterSpacing: "-0.01em",
    color: theme.palette.text.primary,
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: 13,
    lineHeight: "18px",
    color: theme.palette.text.secondary,
  },
  content: {
    padding: "12px 22px 6px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    overflowY: "visible",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  label: {
    fontSize: 12.5,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  placeholder: {
    color: theme.palette.text.disabled,
  },
  connectionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    fontSize: 14,
  },
  connectionName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  status: {
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    "&::before": {
      content: '""',
      width: 7,
      height: 7,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
  },
  online: {
    "&::before": { backgroundColor: "#25d366" },
  },
  offline: {
    "&::before": { backgroundColor: "#e5484d" },
  },
  actions: {
    padding: "12px 22px 18px",
    gap: 6,
  },
  cancel: {
    textTransform: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  confirm: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 150,
    borderRadius: 20,
    boxShadow: "none",
  },
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          // .get(`/whatsapp/filter`, { params: { companyId, session: 0, channel: channelFilter } })
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => {
            // "Conexões que pode atender": sem restrição a lista é a mesma de sempre
            const list = filterAllowedConnections(user, filterOutboundConnections(data));
            setWhatsapps(list);
            // restrito a uma conexão só: já vem escolhida
            if (isConnectionRestricted(user) && Array.isArray(list) && list.length === 1) {
              setSelectedWhatsapp((prev) => (prev === "" ? list[0].id : prev));
            }
          });

        // .then(({ data }) => {
        //   const mappedWhatsapps = data.map((whatsapp) => ({
        //     ...whatsapp,
        //     selected: false,
        //   }));
        //   setWhatsapps(mappedWhatsapps);
        //   if (channelFilter && mappedWhatsapps.length && mappedWhatsapps?.length === 1 && (user.whatsappId === null || user?.whatsapp?.channel !== channelFilter)) {
        //     setSelectedWhatsapp(mappedWhatsapps[0].id)
        //   }
        // });
      };

      // conexão padrão fora das que o usuário pode atender não vem pré-escolhida
      if (whatsappId !== null && whatsappId !== undefined && canSeeTicketConnection(user, { whatsappId })) {
        setSelectedWhatsapp(whatsappId)
      }

      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id)
      }
      fetchContacts();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedContact, channelFilter])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      case "whatsapp_oficial":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      case "webchat":
        return <LanguageIcon style={{ color: "#128C7E", verticalAlign: "middle" }} />;
      default:
        return "error";
    }
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async contactId => {
    if (!contactId) return;
    // if (selectedQueue === "" && user.profile !== 'admin') {
    if (selectedQueue === "") {
      toast.error("Selecione uma fila");
      return;
    }
    // restrito: sem conexão escolhida o servidor usaria a padrão, que pode estar fora da lista
    if (selectedWhatsapp === "" && isConnectionRestricted(user)) {
      toast.error(i18n.t("newTicketModal.connectionRequired"));
      return;
    }

    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      setLoading(false);
      
      const existing = existingTicketFromError(err);
      if (existing && canOpenExistingTicket(existing, user?.id)) {
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
        onClose(existing);
        return;
      }

      if (existing) {
        setOpenAlert(true);
        setUserTicketOpen(existing?.user?.name || "Outro usuário");
        setQueueTicketOpen(existing?.queue?.name || "Sem fila");
      } else {
        // Outros erros
        toastError(err);
      }
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    setSelectedContact(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = option => {
    if (option.number) {
      return <>
        {IconChannel(option.channel)}
        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
          {option.name} - {option.number}
        </Typography>
      </>
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const renderContactAutocomplete = () => {
    if (initialContact === undefined || initialContact.id === undefined) {
      return (
        <div className={classes.field}>
          <span className={classes.label}>Contato</span>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => {
              setChannelFilter(newValue ? newValue.channel : "whatsapp");
              handleSelectOption(e, newValue)
            }}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                size="small"
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading || !selectedContact) return;
                  else if (e.key === "Enter") {
                    handleSaveTicket(selectedContact.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </div>
      )
    }
    return null;
  }

  return (
    <>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        classes={{ paper: classes.paper }}
        aria-labelledby="new-ticket-title"
      >
        <div className={classes.header}>
          <h2 id="new-ticket-title" className={classes.title}>
            {i18n.t("newTicketModal.title")}
          </h2>
          {/* TODO i18n */}
          <p className={classes.subtitle}>
            Escolha o contato, o setor e por qual conexão a conversa vai sair.
          </p>
        </div>
        <DialogContent className={classes.content}>
            {/* CONTATO */}
            {renderContactAutocomplete()}
            {/* FILA */}
            <div className={classes.field}>
              <span className={classes.label}>Setor</span>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                margin="dense"
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === "") {
                    return <span className={classes.placeholder}>Selecione um setor</span>
                  }
                  const queue = user.queues.find(q => q.id === selectedQueue)
                  return queue.name
                }}
              >
                {user.queues?.length > 0 &&
                  user.queues.map((queue, key) => (
                    <MenuItem dense key={key} value={queue.id}>
                      <ListItemText primary={queue.name} />
                    </MenuItem>
                  ))
                }
              </Select>
            </div>
            {/* CONEXAO */}
            <div className={classes.field}>
              <span className={classes.label}>Conexão</span>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                margin="dense"
                value={selectedWhatsapp}
                onChange={(e) => {
                  setSelectedWhatsapp(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === "") {
                    return <span className={classes.placeholder}>Selecione uma conexão</span>
                  }
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp)
                  return whatsapp?.name
                }}
              >
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id}>
                      <div className={classes.connectionRow}>
                        {IconChannel(whatsapp.channel)}
                        <span className={classes.connectionName}>{whatsapp.name}</span>
                        <span className={clsx(classes.status, whatsapp.status === 'CONNECTED' ? classes.online : classes.offline)}>
                          {whatsapp.status === 'CONNECTED' ? "Conectada" : "Desconectada"}
                        </span>
                      </div>
                    </MenuItem>
                  ))}
              </Select>
            </div>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            onClick={handleClose}
            disabled={loading}
            className={classes.cancel}
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="button"
            disabled={!selectedContact}
            onClick={() => handleSaveTicket(selectedContact.id)}
            color="primary"
            loading={loading}
            className={classes.confirm}
          >
            {i18n.t("newTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={handleCloseContactModal}
            onSave={handleAddNewContactTicket}
          ></ContactModal>
        )}
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog >
    </>
  );
};
export default NewTicketModal;